import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import type {
  QualificationSchemeDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { FieldSaveError } from "./FieldSaveError";
import { distinctCategories, repeatsAnEarlierCategory } from "./schemeCategories";
import { endOfDay, startOfUtcDay } from "./schemeValidityDates";
import { SavedPeriodStays, useValidityPeriod } from "./useValidityPeriod";

interface Props {
  eventId: string;
  scheme: QualificationSchemeDto | null;
  isEditable: boolean;
}

// Announced once and binding from then on, which is why the fields lock at publication.
export const QualificationSchemeFields = ({ eventId, scheme, isEditable }: Props) => {
  const { t } = useTranslation();
  const { state, updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const { validFrom, validTo, setValidity, keepsSavedPeriod } = useValidityPeriod(scheme, (period) =>
    updateField({ publicContext: { qualificationScheme: period } }),
  );
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const [rules, setRules] = useState(publicContext?.qualificationSchemeRules ?? "");
  const [categories, setCategories] = useState<string[]>(publicContext?.qualificationSchemeCategories ?? []);

  const saveCategories = (next: string[]) => {
    setCategories(next);
    updateField({ publicContext: { qualificationSchemeCategories: distinctCategories(next) } });
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <Heading as="h3" size="sm" mb="1">
        {t("The qualification system")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="5">
        {t(
          "Announced once. Suppliers can apply for the whole period, and competitions under the system run among those admitted.",
        )}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
        <FormControl>
          <FormLabel fontSize="sm">{t("The system runs from")}</FormLabel>
          <ThemedDatePicker
            date={validFrom}
            isClearable
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validFrom: date ? startOfUtcDay(date) : null })}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">{t("until")}</FormLabel>
          <ThemedDatePicker
            date={validTo}
            isClearable
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validTo: date ? endOfDay(date) : null })}
          />
          <FormHelperText>
            {t("Suppliers can apply right up to this date.")}
          </FormHelperText>
        </FormControl>
      </SimpleGrid>
      {keepsSavedPeriod && scheme && <SavedPeriodStays saved={scheme} />}

      <FormControl mt="6" isRequired>
        <FormLabel htmlFor="qualification-scheme-rules" fontSize="sm">
          {t("Rules for admission and management")}
        </FormLabel>
        <Textarea
          id="qualification-scheme-rules"
          rows={5}
          value={rules}
          isDisabled={!isEditable}
          onChange={(event) => {
            setRules(event.target.value);
            updateField(
              { publicContext: { qualificationSchemeRules: event.target.value.trim() ? event.target.value : null } },
              { debounceMs: 300 },
            );
          }}
        />
        <FieldSaveError eventId={eventId} path="publicContext.qualificationSchemeRules" />
        <FormHelperText>
          {t(
            "How suppliers are admitted, how the list is kept up to date and how long admission lasts. Shown to every supplier — forsyningsforskriften § 13-1 (2).",
          )}
        </FormHelperText>
      </FormControl>

      <FormControl mt="6" isDisabled={!isEditable}>
        <FormLabel fontSize="sm">{t("Categories (optional)")}</FormLabel>
        <Stack spacing="2">
          {categories.map((category, index) => (
            <Box key={index}>
              <HStack>
                <Input
                  size="sm"
                  value={category}
                  aria-label={t("Category {{number}}", { number: index + 1 }) ?? undefined}
                  onChange={(event) =>
                    setCategories((current) => current.map((c, i) => (i === index ? event.target.value : c)))
                  }
                  onBlur={() => saveCategories(categories)}
                />
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label={t("Remove") ?? undefined}
                  icon={<FaTrash />}
                  isDisabled={!isEditable}
                  onClick={() => saveCategories(categories.filter((_, i) => i !== index))}
                />
              </HStack>
              {repeatsAnEarlierCategory(categories, index) && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("This category is already in the list, so it is saved once.")}
                </Text>
              )}
            </Box>
          ))}
        </Stack>
        <Button
          size="sm"
          variant="outline"
          mt="2"
          isDisabled={!isEditable}
          onClick={() => setCategories((current) => [...current, ""])}
        >
          {t("Add category")}
        </Button>
        <FieldSaveError eventId={eventId} path="publicContext.qualificationSchemeCategories" />
        <FormHelperText>
          {t(
            "For example by type of contract. Each admitted supplier can then be placed in the categories it is qualified for — forsyningsforskriften § 13-2.",
          )}
        </FormHelperText>
      </FormControl>
    </Box>
  );
};
